import { useState } from "react"
import { useSimulation } from "@/hooks/useSimulation"
import { useAppStore } from "@/hooks/useAppStore"
import { Badge, Panel } from "@/components/ui"
import { SimulationViewport } from "@/components/simulation/SimulationViewport"
import { TelemetryPanel } from "@/components/simulation/TelemetryPanel"
import { FlightCharts } from "@/components/simulation/FlightCharts"
import { SimulationControlsBar } from "@/components/simulation/SimulationControlsBar"
import { ConfigurationPanel } from "@/components/simulation/ConfigurationPanel"
import { LandingResultPanel } from "@/components/simulation/LandingResultPanel"
import { DefaultConfig } from "@/lib/physics"

function deriveCurrentIndex(simDuration: number, length: number): number {
  if (length === 0) return 0
  const idx = Math.floor(simDuration / 0.1) // outputDt = 0.1s
  return Math.min(Math.max(idx, 0), length - 1)
}

export default function FlightSimulationPage() {
  const {
    start,
    isRunning,
    isPaused,
    isCompleted,
    getTelemetry,
    getLanding,
  } = useSimulation()

  const { addSimLog, setSimStatus, simDuration } = useAppStore()

  const [algorithm] = useState<"hand_rule" | "pid">("hand_rule")
  const [seed] = useState<number>(Date.now() % 10000)
  const [showConfig, setShowConfig] = useState(false)

  const telemetry = getTelemetry()
  const landing = getLanding()

  const currentIndex = deriveCurrentIndex(simDuration, telemetry.length)

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink tracking-tight">Flight Simulation</h1>
          <p className="text-muted text-sm">
            Real physics-driven rocket landing simulator &mdash; telemetry, trajectory, and engine control
          </p>
        </div>
        <div className="flex items-end gap-3">
          <Badge variant={isRunning ? "running" : isCompleted ? "nominal" : isPaused ? "warn" : "muted"}>
            {isRunning ? "RUNNING" : isCompleted ? "COMPLETED" : isPaused ? "PAUSED" : "IDLE"}
          </Badge>
          <span className="text-xs font-mono text-dim mono">
            MET {telemetry[currentIndex]?.t?.toFixed(1) ?? 0}s
          </span>
        </div>
      </div>

      <SimulationControlsBar onConfigChange={() => setShowConfig(true)} />

      {showConfig && (
        <ConfigurationPanel
          onRun={(config) => {
            setShowConfig(false)
            try {
              start(config, algorithm, seed)
            } catch (err) {
              addSimLog(`Simulation error: ${(err as Error).message}`)
              setSimStatus("idle")
            }
          }}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8 xl:col-span-9 gap-4">
          <Panel title="Flight Trajectory &mdash; 2D View" padded={false}>
            <div className="p-1">
              <SimulationViewport
                telemetry={telemetry}
                landing={landing}
                currentIndex={currentIndex}
                targetX={DefaultConfig.landingTargetX}
                targetZoneHalfWidth={DefaultConfig.maxHorizontalError}
                height={560}
              />
            </div>
          </Panel>
        </div>

        <div className="lg:col-span-4 xl:col-span-3 gap-4">
          <TelemetryPanel
            reading={telemetry[currentIndex] ?? null}
            landing={landing}
            maxFuel={DefaultConfig.rocket.maxFuel}
          />

          {landing && (
            <LandingResultPanel result={landing} controller={algorithm} />
          )}
        </div>
      </div>

      <Panel title="Flight Telemetry Charts" padded={false}>
        <div className="p-1">
          <FlightCharts
            telemetry={telemetry}
            currentIndex={currentIndex}
            height={260}
          />
        </div>
      </Panel>
    </div>
  )
}
